import { useEffect, useRef, useCallback } from "react";

interface IdleTimerProps {
  onLogoutPartial: () => void; // Triggered for warning before session ends
  onLogoutComplete: () => void; // Triggered when session ends completely
  onLock: () => void; // Triggered when user is locked after 30 minutes
}

const useIdleTimer = ({ onLogoutPartial, onLogoutComplete, onLock }: IdleTimerProps) => {
  const idleTimers = useRef<{
    activity: NodeJS.Timeout | null;
    lock: NodeJS.Timeout | null;
    complete: NodeJS.Timeout | null;
  }>({
    activity: null,
    lock: null,
    complete: null,
  });

  // Memoize the resetIdleTimer function
  const resetIdleTimer = useCallback(() => {
    // Clear existing timers
    if (idleTimers.current.activity) clearTimeout(idleTimers.current.activity);
    if (idleTimers.current.lock) clearTimeout(idleTimers.current.lock);
    if (idleTimers.current.complete) clearTimeout(idleTimers.current.complete);

    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds
    const lockTime = 30 * 60 * 1000; // 30 minutes in milliseconds
    const partialLogoutTime = oneHour - 5 * 60 * 1000; // 5 minutes before session ends

    // Set new timers
    idleTimers.current.activity = setTimeout(onLogoutPartial, partialLogoutTime); // Trigger partial logout
    idleTimers.current.lock = setTimeout(onLock, lockTime); // Trigger 30-minute lock
    idleTimers.current.complete = setTimeout(onLogoutComplete, oneHour); // Trigger complete logout
  }, [onLogoutPartial, onLogoutComplete, onLock]); // Dependencies of resetIdleTimer

  useEffect(() => {
    const events = ["click", "mousemove", "keypress", "scroll"]; // User activity events

    const eventHandler = () => resetIdleTimer();

    // Add event listeners for user activity
    events.forEach((event) => {
      window.addEventListener(event, eventHandler);
    });

    // Initialize timers
    resetIdleTimer();

    return () => {
      // Cleanup: Remove event listeners and clear timers
      events.forEach((event) => {
        window.removeEventListener(event, eventHandler);
      });

      if (idleTimers.current.activity) clearTimeout(idleTimers.current.activity);
      if (idleTimers.current.lock) clearTimeout(idleTimers.current.lock);
      if (idleTimers.current.complete) clearTimeout(idleTimers.current.complete);
    };
  }, [resetIdleTimer]); // resetIdleTimer is now stable because of useCallback

  return {};
};

export default useIdleTimer;
